"use client"

import { t } from "@/languages/i18n"
import { AlbumImage, AlbumLayout, useAlbumModalStore } from "@/stores/use-album-modal-store"
import { ArrowLeftOutlined } from "@ant-design/icons"
import { useCallback, useEffect, useState } from "react"

import AlbumPreview from "./AlbumPreview"
import ImagePicker from "./ImagePicker"
import { getLayoutOptions } from "./layout-options"
import { buildAlbumHTML } from "./utils"

type Step = "editor" | "preview"

export default function ImageAlbumModal() {
  const { isOpen, layout: initialLayout, editorRef, editingAlbum, close } = useAlbumModalStore()
  const [step, setStep] = useState<Step>("editor")
  const [selectedImages, setSelectedImages] = useState<AlbumImage[]>([])
  const [currentLayout, setCurrentLayout] = useState<AlbumLayout>(initialLayout)
  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null)

  useEffect(() => {
    if (isOpen) {
      if (editingAlbum) {
        setStep("preview")
        setSelectedImages(editingAlbum.images)
        setCurrentLayout(editingAlbum.layout)
      } else {
        setStep("editor")
        setSelectedImages([])
        setCurrentLayout(initialLayout)
      }
      setCurrentFolderId(null)
    }
  }, [isOpen, initialLayout, editingAlbum])

  const handleInsert = useCallback(() => {
    const html = buildAlbumHTML(selectedImages, currentLayout)
    const editor = editorRef

    // Album được lưu như một khối HTML Embed atomic (model element `rawHtml`).
    // CKEditor không phân rã các <img> bên trong nữa → sửa đọc đúng danh sách ảnh,
    // xóa 1-click cả khối, và giữ nguyên <script>/<style> (lightbox, slider).
    if (editingAlbum && editingAlbum.element) {
      // editingAlbum.element trỏ tới bọc widget `.raw-html-embed` (xem bridge parse).
      const widgetDomEl = editingAlbum.element as HTMLElement

      if (editor) {
        try {
          const view = editor.editing.view
          const viewElement = view.domConverter.mapDomToView(widgetDomEl)

          if (viewElement) {
            const modelElement = editor.editing.mapper.toModelElement(viewElement)
            if (modelElement && modelElement.is("element", "rawHtml")) {
              editor.model.change((writer: any) => {
                writer.setAttribute("value", html, modelElement)
              })
            }
          }
        } catch (err) {
          console.error("Failed to update album in CKEditor:", err)
        }
      }
    } else if (editor) {
      try {
        const model = editor.model
        model.change((writer: any) => {
          const rawHtmlEl = writer.createElement("rawHtml", { value: html })
          model.insertContent(rawHtmlEl)
          writer.setSelection(rawHtmlEl, "on")
        })
      } catch {
        console.error(t("Failed to insert album HTML"))
      }
    }
    close()
  }, [selectedImages, currentLayout, editorRef, editingAlbum, close])

  if (!isOpen) return null

  const layoutOptions = getLayoutOptions()
  const layoutLabels = Object.fromEntries(
    layoutOptions.map(o => [o.id, o.label]),
  ) as Record<AlbumLayout, string>

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[99998]"
        onClick={close}
      />

      {/* Modal */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-2xl z-[99999] w-full max-w-[1400px] max-h-[90vh] flex flex-col overflow-hidden">

        <ModalHeader
          step={step}
          selectedCount={selectedImages.length}
          onBack={step === "preview" ? () => setStep("editor") : undefined}
          onClose={close}
          isEditing={!!editingAlbum}
        />

        <div className="flex-1 overflow-hidden min-h-0 flex">
          {step === "editor" && (
            <>
              <LayoutSidebar
                options={layoutOptions}
                currentLayout={currentLayout}
                onSelect={setCurrentLayout}
              />
              <div className="flex-1 flex flex-col min-w-0">
                <ImagePicker selected={selectedImages} onChange={setSelectedImages} />
              </div>
            </>
          )}
          {step === "preview" && (
            <PreviewContent
              images={selectedImages}
              layout={currentLayout}
              layoutLabel={layoutLabels[currentLayout]}
              onReorder={setSelectedImages}
            />
          )}
        </div>

        <ModalFooter
          step={step}
          selectedCount={selectedImages.length}
          onCancel={close}
          onPreview={() => setStep("preview")}
          onInsert={handleInsert}
          isEditing={!!editingAlbum}
        />
      </div>
    </>
  )
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function ModalHeader({ step, selectedCount, onBack, onClose, isEditing }: {
  step: Step
  selectedCount: number
  onBack?: () => void
  onClose: () => void
  isEditing?: boolean
}) {
  return (
    <div className="flex items-center justify-between px-5 py-3.5 border-b border-gray-100 shrink-0">
      <div className="flex items-center gap-2">
        {onBack && (
          <button onClick={onBack} className="bg-none border-none cursor-pointer text-gray-500 p-1 flex items-center hover:text-gray-700 transition-colors">
            <ArrowLeftOutlined />
          </button>
        )}
        <span className="font-semibold text-[15px] text-gray-800">
          {step === "editor" ? (isEditing ? t("Edit Photo Album") : t("Create Photo Album")) : t("Preview Album")}
        </span>
        {step === "editor" && selectedCount > 0 && (
          <span className="text-xs text-blue-600 font-medium bg-blue-50 px-2 py-0.5 rounded-full">
            {selectedCount} {t("images")}
          </span>
        )}
        {isEditing && (
          <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded-full">
            {t("Editing")}
          </span>
        )}
      </div>
      <button
        onClick={onClose}
        className="bg-none border-none cursor-pointer text-gray-400 p-1 text-lg hover:text-gray-600 transition-colors"
      >
        ✕
      </button>
    </div>
  )
}

function LayoutSidebar({ options, currentLayout, onSelect }: {
  options: ReturnType<typeof getLayoutOptions>
  currentLayout: AlbumLayout
  onSelect: (layout: AlbumLayout) => void
}) {
  return (
    <div className="w-[220px] border-r border-gray-200 shrink-0 overflow-y-auto p-3">
      <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-3">
        {t("Select album style")}
      </div>
      {options.map((opt: ReturnType<typeof getLayoutOptions>[number]) => {
        const isActive = currentLayout === opt.id
        return (
          <div
            key={opt.id}
            onClick={() => onSelect(opt.id)}
            className={`mb-2.5 rounded-lg overflow-hidden cursor-pointer transition-all duration-150 ${isActive
              ? "border-2 border-blue-500 shadow-[0_0_0_2px_rgba(59,130,246,0.2)]"
              : "border-2 border-transparent"
              }`}
          >
            <div className="bg-gray-50">{opt.preview}</div>
            <div className="px-2.5 py-1.5 border-t border-gray-100">
              <div className="font-semibold text-[13px] text-gray-800">{opt.label}</div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function PreviewContent({ images, layout, layoutLabel, onReorder }: {
  images: AlbumImage[]
  layout: AlbumLayout
  layoutLabel: string
  onReorder: (images: AlbumImage[]) => void
}) {
  return (
    <div className="flex-1 overflow-y-auto p-5">
      <div className="max-w-full mx-auto">
        <div className="flex items-center justify-between px-3.5 py-2.5 bg-blue-50 rounded-lg mb-4 text-sm text-gray-700">
          <span><strong>{layoutLabel}</strong> &nbsp;|&nbsp; {images.length} {t("images")}</span>
        </div>
        <div className="border border-dashed border-gray-300 rounded-lg overflow-hidden p-4 bg-gray-50/50">
          <AlbumPreview images={images} layout={layout} onReorder={onReorder} />
        </div>
      </div>
    </div>
  )
}

function ModalFooter({ step, selectedCount, onCancel, onPreview, onInsert, isEditing }: {
  step: Step
  selectedCount: number
  onCancel: () => void
  onPreview: () => void
  onInsert: () => void
  isEditing?: boolean
}) {
  const isEditor = step === "editor"
  const disabled = selectedCount === 0

  return (
    <div className="flex justify-end gap-2.5 px-5 py-3 border-t border-gray-100 shrink-0">
      <button
        onClick={onCancel}
        className="px-4 py-2 rounded-md border border-gray-200 bg-white text-gray-700 text-sm cursor-pointer hover:bg-gray-50 transition-colors"
      >
        {t("Cancel")}
      </button>
      {isEditor && !isEditing && (
        <button
          onClick={onPreview}
          disabled={disabled}
          className={`px-4 py-2 rounded-md border-none text-white text-sm cursor-pointer transition-colors ${disabled ? "bg-gray-300 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
            }`}
        >
          {t("Preview")} →
        </button>
      )}
      {!isEditor || isEditing ? (
        <button
          onClick={onInsert}
          className="px-4 py-2 rounded-md border-none bg-green-500 text-white text-sm cursor-pointer hover:bg-green-600 transition-colors"
        >
          {isEditing ? t("Save changes") : t("Insert Album")}
        </button>
      ) : null}
    </div>
  )
}
